import { cleanIban, isIbanFormat } from '@/helper/iban';
import { EnergyTypesJoules } from '@/helper/utils';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export class ProxyInputValidationError extends Error {
  readonly statusCode = 422;

  constructor(
    message: string,
    public readonly field?: string
  ) {
    super(message);
    this.name = 'ProxyInputValidationError';
  }
}

export const PROXY_TEXT_MAX = 100;
export const PROXY_SEARCH_TERM_MAX = 200;
export const PROXY_SEARCH_TERM_MIN_SUGGEST = 1;
export const PROXY_SEARCH_TERM_MIN_SEARCH = 3;
export const PROXY_PAGINATION_MAX = 100;
export const PROXY_RESPONSE_LIMIT_MAX = 100;
export const PROXY_CONSUMPTION_MIN = 1;
export const PROXY_CONSUMPTION_MAX = 10_000_000;
export const PROXY_ID_MAX_LEN = 20;
export const PROXY_POLL_ID_MAX_LEN = 128;

const SAFE_TEXT_PATTERN = /^[\p{L}\p{N}\s.,\-/()]+$/u;
export const PROVIDER_TEXT_PATTERN = /^[^\u0000-\u001F\u007F$]+$/u;
const LOAD_PROFILE_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
const NUMERIC_STORY_ID_PATTERN = /^\d{1,20}$/;
export const GERMAN_POSTCODE_PATTERN = /^\d{5}$/;
/** Matches de-DE locale dates from toLocaleDateString (e.g. 6.8.2026) and padded dd.MM.yyyy */
const GERMAN_CONTRACT_DATE_PATTERN = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
/** b2b-service DATE_FORMAT (SimpleDateFormat dd.MM.yyyy) */
export const B2B_CONTRACT_DATE_FORMAT = 'DD.MM.YYYY';

function fail(message: string, field?: string): never {
  throw new ProxyInputValidationError(message, field);
}

/** @param label Human-readable fragment for the message; defaults to `field`. */
function invalid(field: string, label?: string): never {
  fail(`Invalid ${label ?? field}`, field);
}

function missing(field: string): never {
  fail(`Missing ${field}`, field);
}

function assertSafeTextValue(
  value: string,
  { max, min = 0, field, pattern = SAFE_TEXT_PATTERN }: { max: number; min?: number; field?: string; pattern?: RegExp }
): string {
  if (value.length < min || value.length > max) {
    fail(`Invalid length for ${field ?? 'value'}`, field);
  }
  if (/[\u0000-\u001F\u007F]/.test(value)) {
    fail(`Invalid characters in ${field ?? 'value'}`, field);
  }
  if (value.includes('$')) {
    fail(`Invalid characters in ${field ?? 'value'}`, field);
  }
  if (!pattern.test(value)) {
    fail(`Invalid characters in ${field ?? 'value'}`, field);
  }
  return value;
}

export function assertGermanPostcode(value: unknown, field = 'postcode'): string {
  if (typeof value !== 'string') {
    invalid(field);
  }
  const trimmed = value.trim();
  if (!GERMAN_POSTCODE_PATTERN.test(trimmed)) {
    invalid(field);
  }
  return trimmed;
}

export function assertOptionalSafeText(
  value: unknown,
  options: { max?: number; min?: number; field?: string } = {}
): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value !== 'string') {
    invalid(options.field ?? 'value');
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return undefined;
  }
  return assertSafeTextValue(trimmed, {
    max: options.max ?? PROXY_TEXT_MAX,
    min: options.min ?? 1,
    field: options.field,
  });
}

export function assertRequiredSafeText(
  value: unknown,
  options: { max?: number; min?: number; field?: string } = {}
): string {
  const result = assertOptionalSafeText(value, { ...options, min: options.min ?? 1 });
  if (!result) {
    missing(options.field ?? 'value');
  }
  return result;
}

export function assertOptionalProviderText(
  value: unknown,
  options: { max?: number; min?: number; field?: string } = {}
): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value !== 'string') {
    invalid(options.field ?? 'value');
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return undefined;
  }
  return assertSafeTextValue(trimmed, {
    max: options.max ?? PROXY_TEXT_MAX,
    min: options.min ?? 1,
    field: options.field,
    pattern: PROVIDER_TEXT_PATTERN,
  });
}

export function assertRequiredProviderText(
  value: unknown,
  options: { max?: number; min?: number; field?: string } = {}
): string {
  const result = assertOptionalProviderText(value, { ...options, min: options.min ?? 1 });
  if (!result) {
    missing(options.field ?? 'value');
  }
  return result;
}

export function assertResponseLimit(limit: unknown): number {
  if (limit === -1) {
    return PROXY_RESPONSE_LIMIT_MAX;
  }
  if (typeof limit !== 'number' || !Number.isInteger(limit)) {
    invalid('limit');
  }
  if (limit < 1 || limit > PROXY_RESPONSE_LIMIT_MAX) {
    invalid('limit');
  }
  return limit;
}

export function assertPaginationPage(page: unknown): number {
  if (typeof page !== 'number' || !Number.isInteger(page)) {
    invalid('paginationPage');
  }
  if (page < 0 || page > PROXY_PAGINATION_MAX) {
    invalid('paginationPage');
  }
  return page;
}

export function assertSearchTerm(term: unknown, { minLen }: { minLen: number }): string {
  if (typeof term !== 'string') {
    invalid('searchTerm', 'search term');
  }
  const trimmed = term.trim();
  if (trimmed.length < minLen || trimmed.length > PROXY_SEARCH_TERM_MAX) {
    invalid('searchTerm', 'search term');
  }
  return assertSafeTextValue(trimmed, { max: PROXY_SEARCH_TERM_MAX, min: minLen, field: 'searchTerm' });
}

export function assertIban(iban: unknown): string {
  if (typeof iban !== 'string' || iban.trim() === '') {
    invalid('iban', 'IBAN');
  }
  const cleaned = cleanIban(iban);
  if (!isIbanFormat(cleaned)) {
    invalid('iban', 'IBAN');
  }
  return cleaned;
}

export function assertEnergyType(type: unknown): EnergyTypesJoules | undefined {
  if (type === undefined || type === null || type === '') {
    return undefined;
  }
  if (type !== EnergyTypesJoules.ELECTRICITY && type !== EnergyTypesJoules.GAS) {
    invalid('type', 'energy type');
  }
  return type;
}

export function assertNumericStoryId(id: unknown): string {
  if (typeof id !== 'string') {
    invalid('id');
  }
  const trimmed = id.trim();
  if (!NUMERIC_STORY_ID_PATTERN.test(trimmed)) {
    invalid('id');
  }
  return trimmed;
}

export function assertPollId(id: unknown): string {
  if (typeof id !== 'string') {
    invalid('id', 'poll id');
  }
  const trimmed = id.trim();
  if (!trimmed || trimmed.length > PROXY_POLL_ID_MAX_LEN) {
    invalid('id', 'poll id');
  }
  // Reject operator-like ids; value is used in equality queries only.
  if (trimmed.startsWith('$')) {
    invalid('id', 'poll id');
  }
  return trimmed;
}

export function assertAnnualConsumption(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
    invalid('annualConsumption', 'annual consumption');
  }
  if (value < PROXY_CONSUMPTION_MIN || value > PROXY_CONSUMPTION_MAX) {
    invalid('annualConsumption', 'annual consumption');
  }
  return value;
}

function parseContractStartDate(date: string): dayjs.Dayjs | null {
  const trimmed = date.trim();

  const iso = dayjs(trimmed, 'YYYY-MM-DD', true);
  if (iso.isValid()) {
    return iso;
  }

  const germanMatch = GERMAN_CONTRACT_DATE_PATTERN.exec(trimmed);
  if (germanMatch) {
    const day = Number(germanMatch[1]);
    const month = Number(germanMatch[2]);
    const year = Number(germanMatch[3]);
    const parsed = dayjs(new Date(year, month - 1, day));
    if (parsed.isValid() && parsed.date() === day && parsed.month() === month - 1 && parsed.year() === year) {
      return parsed;
    }
  }

  return null;
}

/**
 * Validates contract start date and returns dd.MM.yyyy for b2b-service (DATE_FORMAT).
 * Accepts ISO (form value) and de-DE locale strings from toLocaleDateString (e.g. 6.8.2026).
 */
export function assertContractStartDate(date: unknown): string {
  if (typeof date !== 'string' || date.trim() === '') {
    invalid('startDate', 'contract start date');
  }
  const trimmed = date.trim();
  const parsed = parseContractStartDate(trimmed);
  if (!parsed) {
    invalid('startDate', 'contract start date');
  }
  const minDate = dayjs().add(3, 'week').startOf('day');
  const maxDate = dayjs().add(1, 'year').endOf('day');
  if (parsed.isBefore(minDate, 'day') || parsed.isAfter(maxDate, 'day')) {
    invalid('startDate', 'contract start date');
  }
  return parsed.format(B2B_CONTRACT_DATE_FORMAT);
}

export function assertLoadProfile(key: unknown): string {
  if (typeof key !== 'string' || key.trim() === '') {
    invalid('loadProfile', 'load profile');
  }
  const trimmed = key.trim();
  if (!LOAD_PROFILE_PATTERN.test(trimmed)) {
    invalid('loadProfile', 'load profile');
  }
  return trimmed;
}

export function assertBoolean(value: unknown, field = 'value'): boolean {
  if (typeof value !== 'boolean') {
    invalid(field);
  }
  return value;
}
